import {
  useGetCallbackIntegrations,
  useGetIntegrationDetails
} from "@/api/useIntegrations"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { getDisplayMessage } from "@/helpers/error.helper"
import { useQueryClient } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { useStatus } from "@/hooks/useStatus"
import {
  getAsanaWorkspaces,
  toLoggedCallbackParams
} from "@/pages/IntegrationsPage/helpers/ticketing"
import { useEffect, useState } from "react"
import { Navigate, useSearchParams } from "react-router-dom"
import { useIntegrationCallbackParams } from "../../../hooks/useIntegrationCallbackParams"
import { IntegrationLoader } from "../../molecules/IntegrationLoader"
import { StatusComponent } from "./StatusComponent"

import type { Status } from "@/pages/IntegrationsPage"

export const IntegrationStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const operationStatusName = "operationStatus"
  const hasOperationStatus = searchParams.get(
    operationStatusName
  ) as Status | null
  const { status, setStatus } = useStatus(hasOperationStatus || "LOADING")
  const { values, error } = useIntegrationCallbackParams()
  const { valid, invalidReason } = error
  const { vendor } = values
  const queryClient = useQueryClient()
  const showsVendorError = vendor === INTEGRATION_VENDORS.asana
  const [failure, setFailure] = useState<unknown>()

  const {
    isLoading,
    isError,
    error: callbackError
  } = useGetCallbackIntegrations(vendor, values, {
    enabled: valid && !hasOperationStatus,
    silentErrors: showsVendorError,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: Infinity
  })
  const { logger, EVENTS } = useLogger()
  const integrationDetails = useGetIntegrationDetails(
    vendor,
    {},
    { enabled: !!vendor }
  )
  const configurationExist =
    !!integrationDetails.response?.data?.configurationExist

  useEffect(() => {
    if (isLoading || hasOperationStatus) {
      return
    }

    const messages = EVENTS.ERROR_EVENTS.INTEGRATIONS
    const meta = {
      extra: toLoggedCallbackParams(values),
      tags: { vendor: vendor || "not_exist" }
    }

    if (!valid) {
      const isActionRequest = invalidReason === "SETUP_ACTION_REQUEST"
      const currentStatus = isActionRequest ? "REQUESTED" : "REDIRECT"

      if (isActionRequest) logger.info(messages.REQUESTED, meta)
      else logger.error(messages.INVALID_PARAMS, meta)

      setStatus(currentStatus)
      return
    }

    const currentStatus: Status = isError ? "ERROR" : "SUCCESS"

    setStatus(currentStatus)
    setSearchParams(
      { vendor, [operationStatusName]: currentStatus },
      { replace: true }
    )

    if (isError) {
      logger.error(messages.ERROR, meta)
      if (showsVendorError) setFailure(callbackError)
    } else {
      logger.info(messages.SUCCESS, meta)
      // The list/details queries fired before the callback registered the
      // install, so their cached snapshots are stale. Invalidate so the
      // integrations page shows the new org without a hard refresh.
      queryClient.invalidateQueries({
        queryKey: [RESOURCES_NAMES.INTEGRATIONS.LIST]
      })
      queryClient.invalidateQueries({
        queryKey: [`${RESOURCES_NAMES.INTEGRATIONS.LIST}/${vendor}`]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  if (status === "REDIRECT") {
    return <Navigate to="/" />
  }

  if (isLoading || integrationDetails.isLoading) {
    return <IntegrationLoader />
  }

  return (
    <StatusComponent
      configurationExist={configurationExist}
      vendor={vendor}
      status={status}
      errorMessage={getDisplayMessage(failure)}
      workspaces={getAsanaWorkspaces(failure)}
    />
  )
}
